import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Avatar,
  Divider,
  Grid,
  IconButton,
  Tooltip,
} from "@mui/material";
import VerifiedIcon from "@mui/icons-material/Verified";
import SearchOffOutlinedIcon from "@mui/icons-material/SearchOffOutlined";
import { useParams } from "react-router-dom";
import ServicesCard from "../ServicesCard";
import SubServicesCard from "../SubServicesCard";
import LoadingAnimation from "../../Animations/LoadingAnimation";
import {
  cardOutline,
  fonts,
  headingFonts,
  notFoundBox,
} from "../../../Styles/Styles";

const ServiceProviderProfile = () => {
  const { id } = useParams();
  const [userData, setUserData] = useState(null);
  const [services, setServices] = useState(null);
  const [subServices, setSubServices] = useState(null);
  const [loading, setLoading] = useState(true); // Added loading state

  const fetchUserData = async () => {
    if (!id) {
      console.error("User ID is undefined.");
      return;
    }

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_SERVER_BASE_URL}/profile/serviceprovider/${id}`
      );
      if (!response.ok) {
        throw new Error(`Failed to fetch user data: ${response.statusText}`);
      }
      const data = await response.json();
      setUserData(data.serviceProvider);
      setServices(data.services);
      setSubServices(data.subservices);
    } catch (error) {
      console.error("Error fetching user data:", error);
    } finally {
      setLoading(false); // Stop loading when fetch completes
    }
  };

  useEffect(() => {
    fetchUserData();
  }, [id]);

  // Show Loading Indicator
  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "200px",
        }}
      >
        <LoadingAnimation />
      </Box>
    );
  }
  console.log(userData);
  return (
    <Box sx={{ mb: 5, p: 4, backgroundColor: "background.default" }}>
      <Grid container spacing={4}>
        {/* User Info Section */}
        <Grid item xs={12} md={3}>
          <Box
            sx={{
              backgroundColor: "white",
              borderRadius: 2,
              p: 3,
              textAlign: "center",
              ...cardOutline,
            }}
          >
            <Avatar
              src={
                userData.image ||
                "https://elasticbeanstalk-us-east-1-908027412287.s3.us-east-1.amazonaws.com/uploads/ProfileImages/default.png"
              }
              alt={userData.name}
              sx={{
                width: 100,
                height: 100,
                mx: "auto",
                mb: 2,
                fontSize: 32,
                color: "white",
              }}
            />
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <Typography variant="h5" sx={{ ...headingFonts }}>
                {userData.name}
              </Typography>
              {userData.isVerified && (
                <Tooltip
                  title="This Service Providers' identity has been verified."
                  arrow
                >
                  <IconButton
                    size="small"
                    sx={{
                      color: "green",
                      "&:focus, &:active": { outline: "none" },
                    }}
                  >
                    <VerifiedIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
            </Box>
            <Typography variant="body1" gutterBottom sx={{ ...fonts }}>
              @{userData.username}
            </Typography>
          </Box>
          <Box
            sx={{
              p: 3,
              mt: 3,
              backgroundColor: "white",
              borderRadius: 2,
              ...cardOutline,
            }}
          >
            <Typography variant="h6" gutterBottom sx={{ ...headingFonts }}>
              About
            </Typography>
            <Typography variant="body2" gutterBottom sx={{ ...fonts }}>
              {userData.description}
            </Typography>
          </Box>
        </Grid>

        {/* Ads List Section */}
        <Grid item xs={12} md={9}>
          <Box>
            {services.length > 0 ? (
              <>
                <Typography variant="h6" gutterBottom sx={{ ...headingFonts }}>
                  Services
                </Typography>
                <Grid container spacing={3}>
                  {services.map((service, index) => (
                    <Grid item xs={12} sm={6} md={4} key={index}>
                      <ServicesCard
                        service={service}
                        serviceProviderId={userData.id}
                      />
                    </Grid>
                  ))}
                </Grid>
              </>
            ) : (
              <Box sx={{ ...notFoundBox }}>
                <SearchOffOutlinedIcon
                  sx={{
                    color: "var(--primary-color)",
                    fontSize: 50,
                    opacity: 0.5,
                    marginBottom: 1,
                  }}
                />
                <Typography
                  variant="h6"
                  sx={{ color: "var(--primary-color)", ...headingFonts }}
                >
                  No services available.
                </Typography>
              </Box>
            )}
          </Box>
          <Divider sx={{ mt: 5 }} />

          <Box mt={5}>
            {subServices.length > 0 ? (
              <>
                <Typography variant="h6" gutterBottom sx={{ ...headingFonts }}>
                  Specialist in:
                </Typography>
                <Grid container spacing={3}>
                  {subServices.map((service, index) => (
                    <Grid item xs={12} sm={6} md={4} key={index}>
                      <SubServicesCard
                        serviceId={service.parentServiceId}
                        subService={service}
                        serviceProviderId={userData.id}
                      />
                    </Grid>
                  ))}
                </Grid>
              </>
            ) : (
              <Box sx={{ ...notFoundBox }}>
                <SearchOffOutlinedIcon
                  sx={{
                    color: "var(--primary-color)",
                    fontSize: 50,
                    opacity: 0.5,
                    marginBottom: 1,
                  }}
                />
                <Typography
                  variant="h6"
                  sx={{
                    color: "var(--primary-color)",
                    ...headingFonts,
                  }}
                >
                  No specializations listed.
                </Typography>
              </Box>
            )}
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};

export default ServiceProviderProfile;
